/**
 * WebSocket event logger utility for debugging purposes.
 * Stores all incoming WebSocket events in localStorage for analysis.
 * Only intended for development use.
 */

const WS_EVENTS_STORAGE_KEY = 'saaga_ws_events_debug'
const MAX_EVENTS = 1000

interface StoredWebSocketEvent {
  timestamp: string
  event: unknown
}

export const logWebSocketEvent = (event: unknown): void => {
  try {
    const stored = localStorage.getItem(WS_EVENTS_STORAGE_KEY)
    const eventList: StoredWebSocketEvent[] = stored ? JSON.parse(stored) : []

    eventList.push({
      timestamp: new Date().toISOString(),
      event,
    })

    if (eventList.length > MAX_EVENTS) {
      eventList.splice(0, eventList.length - MAX_EVENTS)
    }

    localStorage.setItem(WS_EVENTS_STORAGE_KEY, JSON.stringify(eventList))
  } catch {
    // Silent fail - this is debug tooling
  }
}

export const getWebSocketEventLog = (): StoredWebSocketEvent[] => {
  try {
    const stored = localStorage.getItem(WS_EVENTS_STORAGE_KEY)
    return stored ? JSON.parse(stored) : []
  } catch {
    return []
  }
}

export const getWebSocketEventCount = (): number => {
  try {
    const stored = localStorage.getItem(WS_EVENTS_STORAGE_KEY)
    const eventList: StoredWebSocketEvent[] = stored ? JSON.parse(stored) : []
    return eventList.length
  } catch {
    return 0
  }
}

export const clearWebSocketEventLog = (): void => {
  localStorage.removeItem(WS_EVENTS_STORAGE_KEY)
}

export const downloadWebSocketEventLog = (): void => {
  const eventList = getWebSocketEventLog()

  if (eventList.length === 0) {
    return
  }

  const blob = new Blob([JSON.stringify(eventList, null, 2)], { type: 'application/json' })
  const url = URL.createObjectURL(blob)
  const timestamp = new Date().toISOString().replace(/[:.]/g, '-')
  const filename = `ws-events-${timestamp}.json`

  const link = document.createElement('a')
  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}
